import { type ChangeEvent, type PropsWithChildren, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { type ZodType, z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup, type SwitchInputGroupProps } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { SwitchInput } from '@interxfi/design-system/components/input/switch-input/switch-input';
import { AppSwitchInputWithLens } from './app-switch-input';

export function getOptionalArraySchema<T extends ZodType>(itemSchema: T) {
  return z.object({
    isProviding: z.boolean(),
    value: z.array(itemSchema).min(0),
  } as const);
}

export function getOptionalSchema<T extends ZodType>(schema: T) {
  return z.object({
    isProviding: z.boolean(),
    value: schema.optional(),
  } as const);
}

interface OptionalFieldBaseProps {
  hint?: SwitchInputGroupProps['hint'];
  hintVariant?: SwitchInputGroupProps['hintVariant'];
  label: string;
}

export interface OptionalFieldWrapper<TValue> {
  isProviding: boolean;
  value: TValue extends Array<infer Items> ? Items[] : TValue | undefined;
}

interface OptionalFieldProps<TValue> extends OptionalFieldBaseProps {
  defaultIsProviding?: boolean;
  defaultValue: NonNullable<TValue>;
  isInverted?: boolean;
  lens: Lens<OptionalFieldWrapper<TValue>>;
  onIsProvidingChange?: (newIsProviding: boolean) => void;
  variant?: 'group' | 'standalone';
}

export function OptionalField<TValue>({
  children,
  defaultIsProviding = false,
  defaultValue,
  hint,
  isInverted,
  hintVariant,
  label,
  lens,
  onIsProvidingChange,
  variant = 'group',
}: PropsWithChildren<OptionalFieldProps<TValue>>) {
  const { reset, setValue, unregister } = useFormContext();
  const { name } = lens.interop();
  const isProvidingInterop = lens.focus('isProviding').narrow<boolean>().interop();
  const isProviding = useWatch({ ...isProvidingInterop, defaultValue: defaultIsProviding });
  const { name: valueName } = (lens as any).focus('value').interop();
  const isProvidingLens = lens.focus('isProviding').narrow<boolean>();

  const handleToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newIsChecked = isInverted ? !e.target.checked : e.target.checked;
      const shouldUnregister = !newIsChecked && !Array.isArray(defaultValue);

      if (shouldUnregister) {
        unregister(valueName);
      }

      const newValue = newIsChecked ? defaultValue : Array.isArray(defaultValue) ? [] : undefined;

      onIsProvidingChange?.(newIsChecked);

      if (name) {
        setValue(
          name,
          {
            isProviding: newIsChecked,
            value: newValue,
          },
          { shouldValidate: true },
        );
      } else {
        reset(
          {
            isProviding: newIsChecked,
            value: newValue,
          },
          {
            keepIsValid: false,
            keepTouched: false,
            keepDirty: false,
            keepErrors: false,
          },
        );
      }
    },
    [name, setValue, defaultValue, isInverted, valueName, unregister, reset, onIsProvidingChange],
  );

  const useStandaloneSwitch = variant === 'standalone';

  return (
    <VerticalStack gap={4}>
      {useStandaloneSwitch ? (
        isInverted ? (
          <SwitchInput checked={!isProviding} label={label} name={isProvidingInterop.name} onChange={handleToggle} />
        ) : (
          <AppSwitchInputWithLens label={label} lens={isProvidingLens} onChange={handleToggle} />
        )
      ) : (
        <SwitchInputGroup hint={hint} hintVariant={hintVariant}>
          <SwitchInput
            checked={Boolean((isProviding && !isInverted) || (!isProviding && isInverted))}
            label={label}
            name={isProvidingInterop.name}
            onChange={handleToggle}
          />
        </SwitchInputGroup>
      )}

      {isProviding ? children : null}
    </VerticalStack>
  );
}
